<script setup lang="ts">
import { useEmailscramble } from '@/lib/emailscramble'
import { createTrackingEvent } from '@/lib/matomo'
import { KButton, KSimpleDialog, KText } from '@sozialinfo/kompass'
import { computed } from 'vue'

const { address, title, trackingUrl } = defineProps<{
  url?: string
  email?: string
  address?: string
  title?: string
  trackingUrl?: string
}>()

const trackJobApplication = createTrackingEvent({
  category: 'job-market',
  action: 'apply-now',
  name: title,
})

async function sendTrackingToProvider(url: string) {
  if (!url)
    return
  return fetch(url, { method: 'GET' })
}

// converts a single-line address to an array of lines (accepts newlines and commas as separator)
const multiLineAddress = computed(() => {
  if (!address || address.trim().length === 0) {
    return []
  }
  else {
    return address
      .split(/[\n,]/)
      .map(line => line.trim())
      .filter(line => line.length > 0)
  }
})

const emailSubject = `Bewerbung als ${title} via sozialinfo.ch`
const emailBody = `Guten Tag\n
Mit grossem Interesse habe ich Ihre Stellenausschreibung "${title}" auf sozialinfo.ch gelesen. Im Anhang finden Sie meine Bewerbungsunterlagen.\n
Bei Rückfragen stehe ich Ihnen gerne zur Verfügung und freue mich auf ein persönliches Kennenlernen.\n
Vielen Dank im Voraus für Ihre Antwort.\n\n
Freundliche Grüsse\n
`
const encodedSubject = encodeURIComponent(emailSubject)
const encodedBody = encodeURIComponent(emailBody)
const emailQueryString = `?subject=${encodedSubject}&body=${encodedBody}`

function decodeLink(event: Event) {
  const mailtoLink = `mailto:${useEmailscramble().decode((event.target as HTMLInputElement).getAttribute('href') || '')}${emailQueryString}`
  location.href = mailtoLink
}

async function handleTracking() {
  if (trackingUrl) {
    sendTrackingToProvider(trackingUrl)
  }
  trackJobApplication()
}
</script>

<template>
  <KSimpleDialog
    class="
      gap-0 px-10 py-16
      sm:max-w-[460px]
    "
    description="Auf welchem Weg möchtest du dich bewerben?"
    to="body"
  >
    <template #trigger>
      <KButton
        class="w-full"
        @click="handleTracking"
      >
        Jetzt bewerben
      </KButton>
    </template>
    <template #content>
      <div class="mt-12 space-y-12">
        <KText
          v-if="multiLineAddress.length > 0"
          tag="p"
          paragraph="label"
        >
          <KText
            tag="span"
            paragraph="label"
            class="font-bold"
          >
            Per Post an:<br>
          </KText>
          <KText
            tag="span"
            paragraph="label"
          >
            <span
              v-for="line in multiLineAddress"
              :key="line"
            >
              {{ line }}<br>
            </span>
          </KText>
        </KText>
        <div
          v-if="email || url"
          class="flex flex-col gap-6"
        >
          <KButton
            v-if="email"
            as="a"
            :href="email"
            @click.prevent="decodeLink"
          >
            per E-Mail
          </KButton>
          <KButton
            v-if="url"
            as="a"
            target="_blank"
            rel="noreferrer"
            :href="url"
          >
            per Onlineformular
          </KButton>
        </div>
      </div>
    </template>
  </KSimpleDialog>
</template>
